import { z } from 'zod';
/**
 * The external-automation ingestion API (Admin ▸ API keys & webhooks) —
 * machine clients, authenticated by API key (`ApiKeyGuard`, no session/
 * CSRF), push leads/contacts/activities/tasks into Orion. Built for an
 * external pipeline (e.g. a Claude-based outreach automation) that already
 * knows what happened and wants to record it directly, rather than Orion
 * inferring it from raw email headers the way Inbox Pulse does.
 *
 * Every endpoint matches an existing Lead/Contact by normalized email
 * within the tenant — never by id, since a stateless automation shouldn't
 * need to track Orion's internal ids. Repeat calls for the same email are
 * safe for leads/contacts (upsert, never overwrites an existing record's
 * fields); activities and tasks are append-only, so the calling automation
 * should avoid re-sending the same event rather than relying on Orion to
 * de-duplicate it.
 */
export const upsertLeadSchema = z.object({
    email: z.string().trim().toLowerCase().email(),
    firstName: z.string().trim().max(100).optional(),
    lastName: z.string().trim().max(100).optional(),
    phone: z.string().trim().max(40).optional(),
    companyName: z.string().trim().max(200).optional(),
    jobTitle: z.string().trim().max(150).optional(),
    website: z.string().trim().max(500).optional(),
    /** Looked up by name within the tenant, created if it doesn't exist yet
     * (same `findFirst`-then-`create` pattern as Inbox Pulse's lead source).
     * Defaults to "Claude" so every automation-sourced lead is attributed
     * consistently without the caller having to pre-create anything. */
    sourceName: z.string().trim().min(1).max(100).default('Claude'),
    /** Logged as a 'note' Activity on the lead when it's first created. */
    notes: z.string().trim().max(4000).optional(),
});
/** `status` distinguishes the upsert's two originally-possible outcomes
 * ('existing': matched an existing lead by email, no AI involved at all;
 * 'created': a genuinely new lead) from the three an AI-qualification
 * gate can now produce when `leadId` doesn't exist yet — 'needs_review'/
 * 'rejected'/'error' all mean no Lead was created, `leadId` is null, and
 * `qualificationId` points at the `AiLeadQualification` row (AI Lead
 * Review) explaining why. */
export const upsertLeadResponseSchema = z.object({
    leadId: z.string().uuid().nullable(),
    created: z.boolean(),
    status: z.enum(['existing', 'created', 'needs_review', 'rejected', 'error']),
    qualificationId: z.string().uuid().nullable(),
});
export const upsertContactSchema = z.object({
    email: z.string().trim().toLowerCase().email(),
    firstName: z.string().trim().max(100).optional(),
    lastName: z.string().trim().max(100).optional(),
    phone: z.string().trim().max(40).optional(),
    jobTitle: z.string().trim().max(150).optional(),
    notes: z.string().trim().max(4000).optional(),
});
export const upsertContactResponseSchema = z.object({ contactId: z.string().uuid(), created: z.boolean() });
export const logIntegrationActivitySchema = z.object({
    email: z.string().trim().toLowerCase().email(),
    kind: z.enum(['email', 'note', 'call', 'meeting']).default('email'),
    subject: z.string().trim().max(300).optional(),
    bodyText: z.string().trim().min(1).max(10_000),
    direction: z.enum(['inbound', 'outbound']).optional(),
    occurredAt: z.string().datetime().optional(),
});
export const logIntegrationActivityResponseSchema = z.object({ activityId: z.string().uuid() });
export const createIntegrationTaskSchema = z.object({
    email: z.string().trim().toLowerCase().email(),
    title: z.string().trim().min(1).max(300),
    notes: z.string().trim().max(4000).optional(),
    dueAt: z.string().datetime().optional(),
    /** Who gets the task — looked up by email among active workspace members.
     * Falls back to the matched lead/contact's current owner, and finally to
     * unassigned (never fails the call over a missing owner). */
    ownerEmail: z.string().trim().toLowerCase().email().optional(),
});
export const createIntegrationTaskResponseSchema = z.object({ taskId: z.string().uuid() });
//# sourceMappingURL=schemas.js.map